import { z } from 'zod';
import {
  MENU_ALLERGEN_IDS,
  MENU_ADDITIVE_IDS,
  MENU_DIETARY_IDS,
  getMenuTagDefinition,
  resolveMenuTagId,
} from './menuLegalRegistry';
import { normalizeGuestMenuItemCode } from './guestMenuItemCode';
import { MenuPriceOptionsSchema, normalizeMenuPriceOptions } from './menuPriceOptions';

const knownTagIds = [...MENU_ALLERGEN_IDS, ...MENU_ADDITIVE_IDS, ...MENU_DIETARY_IDS];

export const MenuExtractionItemSchema = z
  .object({
    /** Optional guest-facing dish code (e.g. "12", "N1", "01"). */
    code: z.string().trim().max(32).optional().default(''),
    name: z.string().trim().min(1),
    description: z.string().trim().optional().default(''),
    /**
     * Legacy primary price. Prefer `priceOptions` only; when both exist,
     * options win and this is overwritten from options[0].
     */
    priceCents: z.number().int().nonnegative().optional(),
    /**
     * Sole price source: one unlabeled row for a single price, or labeled sizes.
     */
    priceOptions: MenuPriceOptionsSchema.optional().default([]),
    currency: z.string().min(1).default('EUR'),
    allergens: z.array(z.string()).default([]),
    additives: z.array(z.string()).default([]),
    dietaryTags: z.array(z.string()).default([]),
    isAvailable: z.boolean().default(true),
  })
  .transform((item) => {
    const priceOptions = normalizeMenuPriceOptions(item.priceCents ?? 0, item.priceOptions);
    return {
      ...item,
      code: normalizeGuestMenuItemCode(item.code),
      priceOptions,
      priceCents: priceOptions[0]?.priceCents ?? 0,
    };
  });

export const MenuExtractionCategorySchema = z.object({
  title: z.string().trim().min(1),
  items: z.array(MenuExtractionItemSchema),
});

export const MenuExtractionResultSchema = z.object({
  name: z.string().trim().min(1).default('Speisekarte'),
  detectedLocale: z.string().min(2).max(16),
  localeConfidence: z.number().min(0).max(1).optional().default(0.8),
  categories: z.array(MenuExtractionCategorySchema).min(1),
});

export type MenuExtractionItem = z.infer<typeof MenuExtractionItemSchema>;
export type MenuExtractionCategory = z.infer<typeof MenuExtractionCategorySchema>;
export type MenuExtractionResult = z.infer<typeof MenuExtractionResultSchema>;

export const MENU_EXTRACTION_ALLOWED_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/json',
] as const;

export type MenuExtractionMimeType = (typeof MENU_EXTRACTION_ALLOWED_MIME_TYPES)[number];

export function isMenuExtractionMimeType(value: string): value is MenuExtractionMimeType {
  return (MENU_EXTRACTION_ALLOWED_MIME_TYPES as readonly string[]).includes(value);
}

/** True for JSON menus even when the browser leaves `File.type` empty. */
export function isMenuJsonUpload(file: { type: string; name?: string }): boolean {
  const mime = file.type.trim().toLowerCase();
  if (
    mime === 'application/json' ||
    mime === 'text/json' ||
    mime === 'application/x-json'
  ) {
    return true;
  }
  // Extension wins when MIME is empty / wrong (common on Windows).
  return Boolean(file.name && /\.json$/i.test(file.name));
}

export function filterKnownTagIds(ids: string[]): string[] {
  const known = new Set(knownTagIds);
  return ids.filter(
    (id) => known.has(id) || /^legend_(allergen|additive)_/i.test(id),
  );
}

/**
 * Keep dietary marks from the document: map known aliases to registry IDs when
 * possible, otherwise preserve the freeform token (house icons / claims).
 */
export function coerceDietaryTagIds(ids: string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of ids) {
    const trimmed = raw.trim();
    if (!trimmed) continue;
    const resolved = resolveMenuTagId(trimmed);
    const id = resolved ?? trimmed;
    if (seen.has(id)) continue;
    // Bare kennzeichnung codes belong in allergen/additive fields, not dietary.
    if (!resolved && (/^[a-z]$/i.test(trimmed) || /^\d{1,2}$/.test(trimmed))) {
      continue;
    }
    if (/^legend_(allergen|additive)_/i.test(id)) {
      const def = getMenuTagDefinition(id);
      if (!def || def.kind !== 'dietary') continue;
    }
    seen.add(id);
    out.push(id);
  }
  return out;
}
